import { useState } from "react";
import Modal from "newui/components/ui/Modal/Modal";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel/TextAreaWithLabel";

interface MemberReasonModalProps {
	isOpen: boolean;
	title: string;
	/** Contextual copy shown above the reason field. */
	body: React.ReactNode;
	confirmLabel: string;
	reasonLabel: string;
	isSubmitting?: boolean;
	onConfirm: (reason: string) => void;
	onClose: () => void;
}

const MAX_REASON_LENGTH = 2000;

/**
 * Confirmation modal that captures a required free-text reason. Shared by the
 * Revoke and Unrevoke member actions — the reason is persisted to the audit
 * trail on the backend.
 */
export const MemberReasonModal: React.FC<MemberReasonModalProps> = ({
	isOpen,
	title,
	body,
	confirmLabel,
	reasonLabel,
	isSubmitting = false,
	onConfirm,
	onClose,
}) => {
	const [reason, setReason] = useState("");

	const trimmed = reason.trim();
	const isValid = trimmed.length > 0;

	const handleClose = (): void => {
		setReason("");
		onClose();
	};

	const handleConfirm = (): void => {
		if (!isValid) {
			return;
		}
		onConfirm(trimmed);
		setReason("");
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={title}
			size="md"
			iconType="warning"
			modalType="standard"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: confirmLabel,
				onClick: handleConfirm,
				width: "full",
				disabled: !isValid || isSubmitting,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: handleClose,
				width: "full",
			}}
		>
			<div className="space-y-3">
				{body}
				<TextAreaWithLabel
					label={reasonLabel}
					required
					rows={3}
					maxLength={MAX_REASON_LENGTH}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder="Enter a reason…"
					testId="member-reason-input"
					hasError={!isValid && reason.length > 0}
					errorText={!isValid && reason.length > 0 ? "A reason is required." : undefined}
				/>
			</div>
		</Modal>
	);
};
